import NoDataDonutChart from "@/components/common/NoDataDonutChart";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboard } from "@/contexts/DashboardContext";
import React, { useState } from "react";
import { Cell, Legend, Pie, PieChart, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";


export const CQD_COLORS_LIGHT: readonly [string, string, string] = [
  "#23407A", // Good = Answered - dark blue
  "#00FEC1", // Poor = Missed - teal
  "#6D8596", // Unclassified = Abandoned - gray
];
export const CQD_COLORS_DARK: readonly [string, string, string] = [
  "#1681F6", // Good = Answered
  "#00FEC1", // Poor = Missed
  "#6D8596", // Unclassified = Abandoned
];

export interface CallQualityDistributionData {
  good: number;
  poor: number;
  unclassified: number;
  /** @deprecated Backward compatibility */
  bad?: number;
  /** @deprecated Backward compatibility */
  na?: number;
}

interface CallQualityDistributionWidgetProps {
  data?: CallQualityDistributionData | null;
  loading?: boolean;
  onQualityClick?: (quality: "Good" | "Poor" | "Unclassified") => void;
}

const CallQualityDistributionWidget: React.FC<CallQualityDistributionWidgetProps> = ({ data, loading, onQualityClick }) => {
  const { theme } = useDashboard();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const good = data?.good ?? 77;
  const poor = data?.poor ?? data?.bad ?? 8;
  const unclassified = data?.unclassified ?? data?.na ?? 15;

  const donutData = [
    { name: "Good", value: good },
    { name: "Poor", value: poor },
    { name: "Unclassified", value: unclassified },
  ];

  const total = good + poor + unclassified;
  const currentColors = theme === "dark" ? CQD_COLORS_DARK : CQD_COLORS_LIGHT;

  return (
    <div className="w-full max-w-[400px] min-w-[200px] rounded-xl border dark:border-[#0C3451] flex flex-col bg-white dark:bg-[#0C3451] dark:text-white shadow-sm overflow-hidden h-full">
      <div className="px-4 pt-3 pb-0.5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-[500] text-primary dark:text-white">
            Call Quality Distribution
          </h3>
          <InfoTooltip
            content="Share of calls by quality classification within the selected period. Good met quality thresholds, Poor did not, Unclassified has insufficient data."
            iconClassName="text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center min-h-[160px] px-2 pb-1 pt-0.5">
        {loading ? (
          <Skeleton className="w-full h-44 rounded-lg" />
        ) : (
          <div className="w-full h-56" style={{ overflow: "visible" }}>
            {total === 0 ? (
              <div className="flex items-center justify-center gap-4 w-full h-full">
                <NoDataDonutChart
                  width={140}
                  height={140}
                  innerRadius={45}
                  outerRadius={58}
                  noDataColor="#E5E7EB"
                  noDataText="No data"
                />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={58}
                    stroke="none"
                    paddingAngle={1.5}
                  >
                    {donutData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={currentColors[index % currentColors.length]}
                        stroke="none"
                        className="outline-none"
                        style={{
                          cursor: "pointer",
                          transformOrigin: "center",
                          transition: "transform 0.25s ease-out, filter 0.25s ease-out",
                          transform: hoveredIndex === index ? "scale(1.06)" : "scale(1)",
                          filter: hoveredIndex === index ? "drop-shadow(0 4px 12px rgba(0,0,0,0.12))" : "none",
                          transformBox: "fill-box",
                          willChange: "transform, filter",
                        }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={() => onQualityClick?.(entry.name as "Good" | "Poor" | "Unclassified")}
                      />
                    ))}
                  </Pie>
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    iconType="rect"
                    wrapperStyle={{
                      right: 0,
                      bottom: 0,
                      padding: 0,
                      margin: 0,
                      whiteSpace: "nowrap",
                      width: "auto",
                      minWidth: 0,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                    formatter={(value, entry, index) => {
                      const dataItem = donutData.find((d) => d.name === value);
                      const val = dataItem?.value ?? 0;
                      const percent = total > 0 ? ((val / total) * 100).toFixed(2) : "0.00";
                      const color = currentColors[index % currentColors.length];
                      return (
                        <span
                          role="button"
                          tabIndex={0}
                          className="inline-flex items-baseline gap-1.5 cursor-pointer select-none text-sm"
                          style={{ color }}
                          onClick={() => onQualityClick?.(value as "Good" | "Poor" | "Unclassified")}
                        >
                          <span>{value}: </span>
                          <span>{percent}%</span>
                        </span>
                      );
                    }}
                  />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => {
                      const percent = total > 0 ? ((value / total) * 100).toFixed(2) : "0.00";
                      return [`${value} (${percent}%)`, name];
                    }}
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        const { name, value } = payload[0].payload;
                        const percent = total > 0 ? ((value / total) * 100).toFixed(2) : "0.00";
                        const idx = donutData.findIndex((d) => d.name === name);
                        const color = currentColors[idx >= 0 ? idx : 0];
                        return (
                          <div className="rounded-xl border border-gray-200 dark:border-[#3C5C73] bg-white dark:bg-[#0C3451] px-3 py-2.5 shadow-lg">
                            <div className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: color }}
                              />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{name}</span>
                            </div>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                              {percent}%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                    cursor={false}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallQualityDistributionWidget;
